import { ReactChild, useMemo } from "react";
import { Row, Typography } from "antd";
import * as ReactFlow from '@xyflow/react';
import { PlayCircleOutlined } from "@ant-design/icons";

import { CustomNode, IDefaultInputInstruction, IDefaultMessage } from "../../interfaces";

import NodeHeader from "../NodeHeader"
import { NodeToLinkData } from "../..";
import AddLinkedNodeButton from "../AddLinkedNodeButton";

const HandleComponent = ReactFlow.Handle;

interface IProps {
  node: CustomNode;
  IconComponent: ReactChild;
  title: string;
  customMessageText?: string;
  showDefaultSourceConnector?: boolean;
  onPressLink?: (data: NodeToLinkData) => void;
  children?: ReactChild;
  defaultMessages: IDefaultMessage[];
  defaultInputInstructions: IDefaultInputInstruction[];
  handlePosition?: number;
}

export default function BaseNodeContainer({
  node,
  IconComponent,
  title,
  customMessageText,
  showDefaultSourceConnector = true,
  onPressLink,
  defaultMessages,
  defaultInputInstructions,
  children,
  handlePosition,
}: IProps) {
  const messageText = useMemo(() => {
    if (node.data.default_message) {
      return defaultMessages.find((m) => m._id === node.data.default_message)?.message;
    }

    return node.data.message;
  }, [node.data.message, node.data.default_message, defaultMessages]);

  const isInitialNode = useMemo(() => {
    return node.data.is_initial_item;
  }, [node]);

  const connections = ReactFlow.useNodeConnections({
    id: node.id,
    handleType: "source",
  });

  const isConnectable = useMemo(() => {
    return connections.length === 0;
  }, [connections]);

  return (
    <>
      {
        !isInitialNode && (
          <HandleComponent
            type='target'
            position={ReactFlow.Position.Left}
            isConnectableStart={false}
            style={{
              top: 20
            }}
          />
        )
      }

      <div
        style={{
          border: `${node.selected ? "2px" : "1px"} solid ${node.selected ? "#4591ef" : "#8492A6"}`,
          background: "#FFFFFF",
          borderRadius: 4,
          boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
        }}
      >
        {
          isInitialNode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: "4px 8px",
                borderBottomRightRadius: 4,
                borderBottomLeftRadius: 4,
                gap: 8,
                backgroundColor: "#3BB037"
              }}
            >
              <PlayCircleOutlined
                style={{
                  color: "#FFFFFF",
                }}
              />

              <Typography.Text
                style={{
                  color: "#FFFFFF",
                }}
              >
                Início do fluxo
              </Typography.Text>
            </div>
          )
        }

        <NodeHeader
          node={node}
          title={title}
          IconComponent={IconComponent}
          defaultInputInstructions={defaultInputInstructions}
          defaultMessages={defaultMessages}
        />

        {
          messageText && (
            <Row
              style={{
                maxWidth: 200,
                padding: "4px 12px 12px",
                whiteSpace: 'pre-wrap',
              }}
            >
              <Typography.Text>{customMessageText || messageText}</Typography.Text>
            </Row>
          )
        }

        {children}
      </div>

      {
        showDefaultSourceConnector && (
          <>
            <HandleComponent
              id="a"
              type="source"
              position={ReactFlow.Position.Right}
              isConnectable={isConnectable}
              style={{
                top: handlePosition,
              }}
            />

            {
              isConnectable && (
                <AddLinkedNodeButton
                  onPress={() => {
                    onPressLink?.({
                      source: node.id,
                      sourceHandle: "a",
                      parentPosition: {
                        x: node.positionAbsoluteX,
                        y: node.positionAbsoluteY,
                      },
                      parentWidth: node.width,
                    });
                  }}
                  style={{
                    right: -40,
                    top: handlePosition || "50%",
                    transform: "translate(0, -50%)",
                  }}
                />
              )
            }
          </>
        )
      }
    </>
  )
}
